import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaSearch } from "react-icons/fa";

import { playerUrl, teamUrl } from "../../Utils/Constants";
import { search as searchApi } from "../../Utils/api";
import "./SiteSearch.css";

/**
 * Global typeahead across athletes and teams.
 *
 * The product had no search box at all for a 25,566-athlete catalogue — the
 * only way in was the filter sidebar on the browse page.
 */
export default function SiteSearch() {
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openList, setOpenList] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (term.trim().length < 2) {
      setResults(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      searchApi(term.trim(), 6)
        .then((data) => {
          if (!cancelled) {
            setResults(data);
            setOpenList(true);
          }
        })
        .catch(() => {
          if (!cancelled) setResults(null);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [term]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) {
        setOpenList(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const go = (to) => {
    setOpenList(false);
    setTerm("");
    navigate(to);
  };

  const submit = (event) => {
    event.preventDefault();
    if (term.trim()) go(`/browse?q=${encodeURIComponent(term.trim())}`);
  };

  const athletes = results?.athletes || [];
  const teams = results?.teams || [];
  const empty = results && !athletes.length && !teams.length;

  return (
    <div className="sitesearch" ref={boxRef}>
      <form className="sitesearch-box" onSubmit={submit} role="search">
        <FaSearch className="sitesearch-icon" aria-hidden="true" />
        <input
          type="search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onFocus={() => results && setOpenList(true)}
          placeholder="Search athletes or teams"
          aria-label="Search athletes or teams"
        />
      </form>

      {openList && (loading || results) ? (
        <div className="sitesearch-results" role="listbox">
          {loading && !results ? (
            <div className="sitesearch-note">Searching…</div>
          ) : null}

          {empty ? (
            <div className="sitesearch-note">No matches for “{term.trim()}”</div>
          ) : null}

          {athletes.length ? (
            <>
              <div className="sitesearch-head">Athletes</div>
              {athletes.map((a) => (
                <button
                  key={`a-${a.player_id}`}
                  className="sitesearch-row"
                  onClick={() => go(playerUrl(a))}
                >
                  {a.image_url ? (
                    <img src={a.image_url} alt="" aria-hidden="true" />
                  ) : (
                    <span className="sitesearch-avatar" aria-hidden="true">
                      {(a.player_name || "?").charAt(0)}
                    </span>
                  )}
                  <span className="sitesearch-label">
                    {a.player_name}
                    <small>
                      {[a.team_name, a.position].filter(Boolean).join(" · ")}
                    </small>
                  </span>
                </button>
              ))}
            </>
          ) : null}

          {teams.length ? (
            <>
              <div className="sitesearch-head">Teams</div>
              {teams.map((t) => (
                <button
                  key={`t-${t.team_id}`}
                  className="sitesearch-row"
                  onClick={() => go(teamUrl(t.sport_slug, t.slug))}
                >
                  {t.team_logo_url ? (
                    <img src={t.team_logo_url} alt="" aria-hidden="true" />
                  ) : (
                    <span className="sitesearch-avatar" aria-hidden="true">
                      {(t.team_name || "?").charAt(0)}
                    </span>
                  )}
                  <span className="sitesearch-label">
                    {t.team_name}
                    <small>{t.sport_name}</small>
                  </span>
                </button>
              ))}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
